import { ThemeOptions } from '@mui/material/styles';
import { baseComponents, baseShape, baseSpacing, baseTypography } from './baseTheme';

/**
 * Default Professional Theme
 * Primary: Blue (#1976d2)
 * Secondary: Pink (#dc004e)
 * Target: General use
 * Optimized for compact, data-dense displays
 */
export const defaultTheme: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
            contrastText: '#fff',
        },
        secondary: {
            main: '#dc004e',
            light: '#f50057',
            dark: '#c51162',
            contrastText: '#fff',
        },
        background: {
            default: '#f5f5f5',
            paper: '#ffffff',
        },
        text: {
            primary: '#2c3e50',
            secondary: '#666666',
        },
        error: {
            main: '#f44336',
        },
        warning: {
            main: '#ff9800',
        },
        info: {
            main: '#2196f3',
        },
        success: {
            main: '#4caf50',
        },
    },
    typography: baseTypography,
    spacing: baseSpacing,
    shape: baseShape,
    components: {
        ...baseComponents,
        MuiIconButton: {
            ...baseComponents?.MuiIconButton,
            styleOverrides: {
                root: {
                    padding: 4,
                },
                sizeSmall: {
                    padding: 2,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    '& .MuiInputBase-input': {
                        padding: '6px 10px',
                        fontSize: '0.8125rem',
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: '0.8125rem',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                },
                input: {
                    padding: '6px 10px',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-input': {
                        padding: '6px 10px',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                size: 'small',
            },
            styleOverrides: {
                select: {
                    padding: '6px 10px',
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiChip: {
            ...baseComponents?.MuiChip,
            styleOverrides: {
                root: {
                    height: 22,
                    fontSize: '0.75rem',
                },
                sizeSmall: {
                    height: 18,
                    fontSize: '0.6875rem',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0 1px 6px rgba(0, 0, 0, 0.08)',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: 12,
                },
                title: {
                    fontSize: '0.875rem',
                },
                subheader: {
                    fontSize: '0.75rem',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '4px 8px',
                    fontSize: '0.8125rem',
                },
                head: {
                    fontWeight: 600,
                    padding: '6px 8px',
                    fontSize: '0.8125rem',
                },
                sizeSmall: {
                    padding: '2px 6px',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                },
                selectLabel: {
                    fontSize: '0.75rem',
                },
                displayedRows: {
                    fontSize: '0.75rem',
                },
                select: {
                    fontSize: '0.75rem',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '40px !important',
                    paddingLeft: '8px !important',
                    paddingRight: '8px !important',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#001529',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 6,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '8px 16px',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 36,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 36,
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    padding: '4px 12px',
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.75rem',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    minHeight: 32,
                    padding: '4px 12px',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: '0.8125rem',
                },
                secondary: {
                    fontSize: '0.75rem',
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 32,
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    width: 28,
                    height: 28,
                    fontSize: '0.875rem',
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontSize: '0.625rem',
                    minWidth: 16,
                    height: 16,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                },
            },
        },
    },
};
